import { useState, useEffect } from "react"
import { LoginForm } from "@/components/auth/login-form"
import { BreachPanel } from "@/components/layout/breach-panel"
import { usePageTitle } from "@/hooks/usePageTitle"
import { Button } from "@repo/ui/primitives/button"
import { Sun, Moon } from "lucide-react"
import { useTheme } from "@/lib/theme.jsx"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

export default function Page() {
  const { t } = useTranslation("login");
  usePageTitle(t("page.title", { defaultValue: "Login" }));
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isBreachRoute = location.pathname === "/breach";
  const [showOverlay, setShowOverlay] = useState(isBreachRoute);

  useEffect(() => {
    setShowOverlay(isBreachRoute);
  }, [isBreachRoute]);

  const handleToggleOverlay = () => {
    if (isBreachRoute) {
      navigate("/login", { replace: true });
    } else {
      navigate("/breach", { replace: true });
    }
  };

  return (
    <div className="login-page relative grid min-h-svh w-full grid-cols-1 overflow-hidden bg-background text-foreground lg:grid-cols-2">
      <div className="absolute right-[var(--ds-space-4)] top-[var(--ds-space-4)] z-20 flex items-center gap-[var(--ds-space-control-gap)]">
        <LanguageSwitcher />
        <Button
          onClick={toggleTheme}
          variant="outline"
          size="icon"
          className="size-[var(--ds-density-icon-button-size)] cursor-pointer justify-center p-0"
          aria-label={t("page.toggleThemeAriaLabel", { defaultValue: "Toggle theme" })}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Functional split-panel transition for the login and public-document views. */}
      <div
        className={`pointer-events-none absolute inset-y-0 left-0 z-10 hidden overflow-hidden border-r border-border bg-muted/50 transition-transform duration-[var(--ds-motion-duration-standard)] ease-[var(--ds-motion-ease-emphasized)] motion-reduce:transition-none lg:block lg:w-1/2 ${showOverlay ? "translate-x-full" : "translate-x-0"
          }`}
      >
        <div className="relative flex h-full w-full items-center justify-center p-[var(--ds-space-10)]">
          <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-primary/20" aria-hidden="true" />
          <div className="relative z-10 mx-auto max-w-md text-center lg:text-left">
            <div className="transition-opacity duration-[var(--ds-motion-duration-standard)] motion-reduce:transition-none">
              {showOverlay ? (
                <>
                  <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                    {t("page.secondaryTitle", { defaultValue: "Breach Notification Workspace" })}
                  </h1>
                  <p className="mt-[var(--ds-space-3)] text-muted-foreground">
                    {t("page.secondaryDescription", { defaultValue: "Create corrective actions and share public documents related to security incidents." })}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                    {t("page.primaryTitle", { defaultValue: "SGRC Management Portal" })}
                  </h1>
                  <p className="mt-[var(--ds-space-3)] text-muted-foreground">
                    {t("page.primaryDescription", { defaultValue: "Manage security, governance, risk and compliance workflows from a single place." })}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 z-20 hidden items-center justify-center lg:flex">
        <Button
          variant="outline"
          className="pointer-events-auto size-[var(--ds-density-control-height-lg)] cursor-pointer rounded-[var(--ds-radius-pill)] border-primary/40 bg-card px-0 text-primary shadow-[var(--shadow-surface)] hover:bg-accent hover:text-accent-foreground"
          onClick={handleToggleOverlay}
        >
          {showOverlay ? (
            <span className="relative group flex items-center justify-center">
              <span className="text-xl">&#8592;</span>
              <span className="pointer-events-none absolute left-1/2 top-full mt-[var(--ds-space-2)] -translate-x-1/2 whitespace-nowrap rounded-[var(--ds-radius-control)] border border-border bg-popover px-[var(--ds-space-3)] py-[var(--ds-space-1)] text-xs text-popover-foreground opacity-0 shadow-[var(--shadow-popover)] transition-opacity duration-[var(--ds-motion-duration-fast)] group-hover:opacity-100 motion-reduce:transition-none">
                <span className="font-semibold">
                  {t("page.overlayToggleLoginTooltip", { defaultValue: "Login" })}
                </span>
              </span>
            </span>
          ) : (
            <span className="relative group flex items-center justify-center">
              <span className="text-xl">&#8594;</span>
              <span className="pointer-events-none absolute left-1/2 top-full mt-[var(--ds-space-2)] -translate-x-1/2 whitespace-nowrap rounded-[var(--ds-radius-control)] border border-border bg-popover px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-xs text-popover-foreground opacity-0 shadow-[var(--shadow-popover)] transition-opacity duration-[var(--ds-motion-duration-fast)] group-hover:opacity-100 motion-reduce:transition-none">
                <span className="flex flex-col items-center leading-tight">
                  <span className="text-sm font-medium">
                    {t("page.overlayToggleBreachTooltipSubtitle1", { defaultValue: "Breach Notification" })}
                  </span>
                  <span className="text-xs">
                    {t("page.overlayToggleBreachTooltipAnd", { defaultValue: "&" })}
                  </span>
                  <span className="text-sm font-medium">
                    {t("page.overlayToggleBreachTooltipSubtitle2", { defaultValue: "Public Documents" })}
                  </span>
                </span>
              </span>
            </span>
          )}
        </Button>
      </div>

      <div className={`relative ${isBreachRoute ? 'flex' : 'hidden'} items-center justify-center overflow-hidden bg-background lg:flex`}>
        {/* Sol tarafta Breach Notification + Public Documents paneli */}
        <div
          className={`absolute inset-0 flex w-full items-center justify-center transition-all duration-[var(--ds-motion-duration-standard)] ease-[var(--ds-motion-ease-emphasized)] motion-reduce:transition-none lg:absolute ${showOverlay ? "opacity-100 translate-y-0" : "lg:opacity-0 lg:translate-y-2 lg:pointer-events-none"
            }`}
        >
          <BreachPanel onMobileSwitch={() => navigate("/login")} />
        </div>
      </div>
      <div className={`relative z-0 flex items-center justify-center p-[var(--ds-space-6)] md:p-[var(--ds-space-10)] ${isBreachRoute ? 'hidden lg:flex' : 'flex'}`}>

        <div
          className={`w-full max-w-md transition-all duration-[var(--ds-motion-duration-standard)] ease-[var(--ds-motion-ease-emphasized)] motion-reduce:transition-none ${showOverlay
            ? "lg:opacity-0 lg:translate-y-2 lg:pointer-events-none"
            : "opacity-100 translate-y-0"
            }`}
        >
          <LoginForm onMobileSwitch={() => navigate("/breach")} />
        </div>
      </div>
    </div>
  )
}
